import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DescriptionOutlined,
  EditOutlined,
} from '@mui/icons-material';
import {
  createFamilyMember,
  createPassportUpload,
  deleteFamilyMember,
  listFamilyMembers,
  parsePassportAutofill,
  updateFamilyMember,
  uploadFileToPresignedUrl,
} from '../../services/familyApi';
import PhoneNumberFields from '../auth/PhoneNumberFields';
import { buildPhoneNumber, splitPhoneNumber } from '../../lib/phone';

const relationshipOptions = ['Myself', 'Spouse', 'Child', 'Sibling', 'Parent'];
const genderOptions = ['Male', 'Female', 'Other'];
const maritalStatusOptions = ['Married', 'Single', 'Divorced', 'Separated', 'Widow(er)'];
const requiredFieldLabels = {
  relationship: 'Relationship',
  maritalStatus: 'Marital status',
  firstName: 'First name',
  lastName: 'Last name',
  nationality: 'Nationality',
  dateOfBirth: 'Date of birth',
  placeOfBirth: 'Place of birth',
  passportNumber: 'Passport number',
  passportIssueDate: 'Passport issue date',
  passportExpiryDate: 'Passport expiry date',
  issuingCountry: 'Issuing country',
  issuingAuthority: 'Issuing authority',
  addressPerPassport: 'Address per passport',
  phoneLocalNumber: 'Phone number',
  email: 'Email',
  genderPerPassport: 'Gender per passport',
  fullNamePerPassport: 'Full name per passport',
  passportUploadObjectKey: 'Passport photo',
};

const initialFormState = {
  relationship: 'Myself',
  maritalStatus: '',
  nationalIdNumber: '',
  occupation: '',
  firstName: '',
  middleNames: '',
  lastName: '',
  fullNamePerPassport: '',
  nationality: '',
  dateOfBirth: '',
  placeOfBirth: '',
  passportNumber: '',
  passportIssueDate: '',
  passportExpiryDate: '',
  issuingCountry: '',
  issuingAuthority: '',
  isMachineReadableZonePresent: false,
  passportParseConfidence: 0,
  passportParseWarnings: [],
  addressPerPassport: '',
  countryCode: '+20',
  phoneLocalNumber: '',
  email: '',
  genderPerPassport: '',
  passportUploadObjectKey: '',
  passportFileName: '',
};

function getAccountContactFields(account) {
  const parsedPhone = splitPhoneNumber(account?.phoneNumber);

  return {
    countryCode: parsedPhone.phoneLocalNumber
      ? parsedPhone.countryCode
      : initialFormState.countryCode,
    phoneLocalNumber: parsedPhone.phoneLocalNumber || '',
    email: account?.email || '',
  };
}

function autofillMyselfContact(form, account) {
  if (form.relationship !== 'Myself') {
    return form;
  }

  const accountContact = getAccountContactFields(account);

  return {
    ...form,
    countryCode: accountContact.phoneLocalNumber ? accountContact.countryCode : form.countryCode,
    phoneLocalNumber: accountContact.phoneLocalNumber || form.phoneLocalNumber,
    email: accountContact.email || form.email,
  };
}

function formatMemberName(member) {
  return [member.firstName, member.middleNames, member.lastName].filter(Boolean).join(' ');
}

function formatValidationSummary(labels) {
  if (labels.length <= 4) {
    return labels.join(', ');
  }

  return `${labels.slice(0, 4).join(', ')}, and ${labels.length - 4} more`;
}

function mapMemberToForm(member) {
  const parsedPhone = splitPhoneNumber(member.phoneNumber);

  return {
    ...initialFormState,
    relationship: member.relationship || 'Spouse',
    maritalStatus: member.maritalStatus || '',
    nationalIdNumber: member.nationalIdNumber || '',
    occupation: member.occupation || '',
    firstName: member.firstName || '',
    middleNames: member.middleNames || '',
    lastName: member.lastName || '',
    fullNamePerPassport: member.fullNamePerPassport || '',
    nationality: member.nationality || '',
    dateOfBirth: member.dateOfBirth || '',
    placeOfBirth: member.placeOfBirth || '',
    passportNumber: member.passportNumber || '',
    passportIssueDate: member.passportIssueDate || '',
    passportExpiryDate: member.passportExpiryDate || '',
    issuingCountry: member.issuingCountry || '',
    issuingAuthority: member.issuingAuthority || '',
    isMachineReadableZonePresent: Boolean(member.isMachineReadableZonePresent),
    passportParseConfidence: typeof member.passportParseConfidence === 'number' ? member.passportParseConfidence : 0,
    passportParseWarnings: member.passportParseWarnings || [],
    addressPerPassport: member.addressPerPassport || '',
    countryCode: parsedPhone.countryCode || initialFormState.countryCode,
    phoneLocalNumber: parsedPhone.phoneLocalNumber || '',
    email: member.email || '',
    genderPerPassport: member.genderPerPassport || '',
  };
}

function buildMemberPayload(form) {
  return {
    relationship: form.relationship,
    maritalStatus: form.maritalStatus,
    nationalIdNumber: form.nationalIdNumber.trim(),
    occupation: form.occupation.trim(),
    firstName: form.firstName.trim(),
    middleNames: form.middleNames.trim(),
    lastName: form.lastName.trim(),
    fullNamePerPassport: form.fullNamePerPassport.trim(),
    nationality: form.nationality.trim(),
    dateOfBirth: form.dateOfBirth,
    placeOfBirth: form.placeOfBirth.trim(),
    passportNumber: form.passportNumber.trim(),
    passportIssueDate: form.passportIssueDate,
    passportExpiryDate: form.passportExpiryDate,
    issuingCountry: form.issuingCountry.trim(),
    issuingAuthority: form.issuingAuthority.trim(),
    isMachineReadableZonePresent: form.isMachineReadableZonePresent,
    passportParseConfidence: form.passportParseConfidence,
    passportParseWarnings: form.passportParseWarnings,
    addressPerPassport: form.addressPerPassport.trim(),
    phoneNumber: buildPhoneNumber(form.countryCode, form.phoneLocalNumber),
    email: form.email.trim(),
    genderPerPassport: form.genderPerPassport,
  };
}

function validateMemberForm(form, hasMyselfConflict, options = {}) {
  const { requirePassportUpload = true } = options;
  const errors = {};
  const trimmedValues = Object.fromEntries(
    Object.entries(form).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
  );

  if (!trimmedValues.relationship) {
    errors.relationship = 'Select a relationship.';
  }

  if (!trimmedValues.maritalStatus) {
    errors.maritalStatus = 'Select a marital status.';
  }

  if (trimmedValues.relationship === 'Myself' && hasMyselfConflict) {
    errors.relationship = 'The Myself profile can only be added once.';
  }

  [
    ['firstName', 'First name is required.'],
    ['lastName', 'Last name is required.'],
    ['nationality', 'Nationality is required.'],
    ['dateOfBirth', 'Date of birth is required.'],
    ['placeOfBirth', 'Place of birth is required.'],
    ['passportNumber', 'Passport number is required.'],
    ['passportIssueDate', 'Passport issue date is required.'],
    ['passportExpiryDate', 'Passport expiry date is required.'],
    ['issuingCountry', 'Issuing country is required.'],
    ['issuingAuthority', 'Issuing authority is required.'],
    ['addressPerPassport', 'Address per passport is required.'],
    ['phoneLocalNumber', 'Phone number is required.'],
    ['email', 'Email is required.'],
    ['genderPerPassport', 'Gender per passport is required.'],
    ['fullNamePerPassport', 'Full name per passport is required.'],
  ].forEach(([field, message]) => {
    if (!trimmedValues[field]) {
      errors[field] = message;
    }
  });

  if (buildPhoneNumber(trimmedValues.countryCode, trimmedValues.phoneLocalNumber).length < 8) {
    errors.phoneLocalNumber = 'Enter a valid phone number.';
  }

  if (trimmedValues.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/i.test(trimmedValues.email)) {
    errors.email = 'Enter a valid email address.';
  }

  ['dateOfBirth', 'passportIssueDate', 'passportExpiryDate'].forEach((field) => {
    if (trimmedValues[field] && !/^\d{4}-\d{2}-\d{2}$/.test(trimmedValues[field])) {
      errors[field] = 'Use YYYY-MM-DD.';
    }
  });

  if (requirePassportUpload && (!trimmedValues.passportUploadObjectKey || !trimmedValues.passportFileName)) {
    errors.passportUploadObjectKey = 'Upload a passport photo to continue.';
  }

  return errors;
}

function FamilyMemberCard({ member, onDelete, onEdit }) {
  return (
    <Box
      onClick={() => onEdit(member)}
      sx={{
        bgcolor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        cursor: 'pointer',
        boxShadow: '0 2px 8px rgba(14, 28, 45, 0.04)',
        display: 'flex',
        minHeight: 94,
        p: 2,
        transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 10px 24px rgba(14, 28, 45, 0.08)',
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Stack justifyContent="center" spacing={0.45} sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
          {formatMemberName(member)}
        </Typography>
        <Typography color="text.secondary" noWrap variant="body2">
          {[member.relationship, member.maritalStatus, member.genderPerPassport].filter(Boolean).join(' • ')}
        </Typography>
        <Typography color="text.secondary" noWrap variant="caption">
          {[member.nationalIdNumber ? `National ID ${member.nationalIdNumber}` : null, member.occupation]
            .filter(Boolean)
            .join(' • ') || `Passport #${member.passportNumber}`}
        </Typography>
      </Stack>
      <Stack alignItems="center" direction="row" spacing={0.25} sx={{ ml: 1 }}>
        <IconButton
          aria-label={`Edit ${formatMemberName(member) || 'family member'}`}
          onClick={(event) => {
            event.stopPropagation();
            onEdit(member);
          }}
          size="small"
        >
          <EditOutlined fontSize="small" />
        </IconButton>
        <IconButton
          aria-label={`Delete ${formatMemberName(member) || 'family member'}`}
          color="error"
          onClick={(event) => {
            event.stopPropagation();
            onDelete(member);
          }}
          size="small"
        >
          <DeleteOutlined fontSize="small" />
        </IconButton>
      </Stack>
    </Box>
  );
}

function AddFamilyMemberTile({ onClick, canAddMyself }) {
  return (
    <Button
      onClick={onClick}
      sx={{
        alignItems: 'center',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: 1.5,
        color: 'text.primary',
        display: 'flex',
        gap: 1.5,
        justifyContent: 'space-between',
        minHeight: 94,
        p: 2,
        textAlign: 'left',
        width: '100%',
      }}
      variant="outlined"
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.2 }}>
          Add family member
        </Typography>
        <Typography color="text.secondary" sx={{ mt: 0.2 }} variant="body2">
          Create another family profile
        </Typography>
      </Box>
      <Avatar
        sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', height: 46, width: 46 }}
      >
        <AddOutlined />
      </Avatar>
    </Button>
  );
}

function FieldText({ children, form, errors, field, label, onChange, ...props }) {
  return (
    <TextField
      error={Boolean(errors[field])}
      fullWidth
      helperText={errors[field] || undefined}
      InputLabelProps={{
        ...props.InputLabelProps,
        shrink: true,
      }}
      label={label}
      onChange={(event) => onChange(field, event.target.value)}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 1.5,
        },
        ...props.sx,
      }}
      value={form[field]}
      variant="outlined"
      {...props}
    >
      {children}
    </TextField>
  );
}

function PassportDropzone({
  onPickFile,
  uploadBusy,
  parseBusy,
  uploadError,
  autofillWarnings,
  stagedFileName,
  previewUrl,
  previewType,
}) {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);
  const hasPreview = Boolean(previewUrl);
  const isBusy = uploadBusy || parseBusy;

  function openFilePicker(event) {
    event?.preventDefault();
    event?.stopPropagation();
    inputRef.current?.click();
  }

  function handleDrop(event) {
    event.preventDefault();
    setDragActive(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      onPickFile(file);
    }
  }

  return (
    <Box
      component={!hasPreview && !isBusy ? 'label' : 'div'}
      onDragEnter={(event) => {
        event.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={(event) => {
        event.preventDefault();
        setDragActive(false);
      }}
      onDragOver={(event) => event.preventDefault()}
      onDrop={handleDrop}
      sx={{
        alignItems: 'center',
        bgcolor: dragActive ? 'secondary.light' : 'background.default',
        border: (theme) => `1.5px dashed ${theme.palette.divider}`,
        borderColor: dragActive ? 'primary.main' : 'divider',
        borderRadius: 1.5,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
        height: '100%',
        justifyContent: hasPreview ? 'flex-start' : 'center',
        minHeight: hasPreview ? 'auto' : 320,
        overflow: 'hidden',
        p: 2.5,
        position: 'relative',
        textAlign: 'center',
        width: '100%',
      }}
    >
      <input
        accept="image/*,application/pdf"
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            onPickFile(file);
          }
          event.target.value = '';
        }}
        ref={inputRef}
        style={{ display: 'none' }}
        type="file"
      />

      {hasPreview && previewType?.startsWith('image/') ? (
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: '1 1 auto',
            justifyContent: 'center',
            minHeight: 0,
            width: '100%',
          }}
        >
          <Box
            alt={stagedFileName || 'Passport preview'}
            component="img"
            src={previewUrl}
            sx={{
              borderRadius: 1,
              display: 'block',
              maxHeight: '100%',
              maxWidth: '100%',
              objectFit: 'contain',
              width: '100%',
            }}
          />
        </Box>
      ) : null}

      {isBusy ? (
        <Stack
          alignItems="center"
          spacing={1}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <CircularProgress size={24} />
          <Typography color="text.secondary" variant="body2">
            Hold tight, we&apos;re automatically filling in your passport details.
          </Typography>
        </Stack>
      ) : (
        <Box
          sx={{
            alignItems: 'center',
            color: 'text.primary',
            cursor: !hasPreview ? 'pointer' : 'default',
            display: 'flex',
            flexDirection: 'column',
            flex: hasPreview ? '0 0 auto' : '1 1 auto',
            gap: 1.25,
            maxWidth: 360,
            position: 'relative',
            width: '100%',
            zIndex: 1,
          }}
        >
          {!hasPreview ? (
            <>
              <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', height: 56, width: 56 }}>
                <CloudUploadOutlined />
              </Avatar>

              <Box>
                <Typography variant="h6">Upload passport photo</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }} variant="body2">
                  Drag and drop the passport photo here. We will take it from here.
                </Typography>
              </Box>
            </>
          ) : null}

          {stagedFileName ? (
            <Stack alignItems="center" direction="row" spacing={1}>
              <DescriptionOutlined fontSize="small" />
              <Typography variant="body2">{stagedFileName}</Typography>
            </Stack>
          ) : null}

          {previewType === 'application/pdf' ? (
            <Chip
              color="primary"
              label="PDF uploaded"
              variant="outlined"
            />
          ) : null}

          {hasPreview ? (
            <Button
              onClick={openFilePicker}
              size="small"
              sx={{
                minWidth: 0,
                p: 0,
                textDecoration: 'underline',
                textUnderlineOffset: '0.18em',
              }}
              variant="text"
            >
              Upload New
            </Button>
          ) : null}
        </Box>
      )}

      {uploadError ? (
        <Alert severity="error" sx={{ position: 'relative', zIndex: 1 }}>
          {uploadError}
        </Alert>
      ) : null}
      {autofillWarnings.length > 0 ? (
        <Alert severity="warning" sx={{ position: 'relative', zIndex: 1 }}>
          {autofillWarnings.join(' ')}
        </Alert>
      ) : null}
    </Box>
  );
}

function FamilyWorkspace({ account }) {
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersError, setMembersError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [activeMemberId, setActiveMemberId] = useState('');
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [uploadBusy, setUploadBusy] = useState(false);
  const [parseBusy, setParseBusy] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [autofillWarnings, setAutofillWarnings] = useState([]);
  const [passportPreviewUrl, setPassportPreviewUrl] = useState('');
  const [passportPreviewType, setPassportPreviewType] = useState('');
  const isEditing = dialogMode === 'edit';

  const hasMyselfMember = useMemo(
    () => members.some((member) => member.relationship === 'Myself'),
    [members]
  );
  const hasMyselfConflict = useMemo(
    () =>
      members.some(
        (member) => member.relationship === 'Myself' && member.memberId !== activeMemberId
      ),
    [activeMemberId, members]
  );
  const formValidationErrors = useMemo(
    () => validateMemberForm(form, hasMyselfConflict, { requirePassportUpload: !isEditing }),
    [form, hasMyselfConflict, isEditing]
  );
  const missingRequiredFieldLabels = useMemo(() => {
    return Object.entries(requiredFieldLabels)
      .filter(([field]) => formValidationErrors[field] && !String(form[field] ?? '').trim())
      .map(([, label]) => label);
  }, [form, formValidationErrors]);
  const showValidationHint =
    !parseBusy && Object.keys(formValidationErrors).length > 0 && !saveError;
  const saveDisabled =
    saveBusy ||
    uploadBusy ||
    parseBusy ||
    !account?.token ||
    Object.keys(formValidationErrors).length > 0;

  useEffect(() => {
    let ignore = false;

    async function loadMembers() {
      if (!account?.token) {
        setMembers([]);
        setMembersLoading(false);
        return;
      }

      setMembersLoading(true);
      setMembersError('');

      try {
        const response = await listFamilyMembers({ token: account.token });
        if (!ignore) {
          setMembers(response.members ?? []);
        }
      } catch (error) {
        if (!ignore) {
          setMembersError(error.message);
        }
      } finally {
        if (!ignore) {
          setMembersLoading(false);
        }
      }
    }

    loadMembers();
    return () => {
      ignore = true;
    };
  }, [account?.token]);

  useEffect(() => () => {
    if (passportPreviewUrl) {
      URL.revokeObjectURL(passportPreviewUrl);
    }
  }, [passportPreviewUrl]);

  function resetDialogState(defaultRelationship) {
    if (passportPreviewUrl) {
      URL.revokeObjectURL(passportPreviewUrl);
    }

    setForm(autofillMyselfContact({
      ...initialFormState,
      relationship: defaultRelationship,
    }, account));
    setDialogMode('create');
    setActiveMemberId('');
    setErrors({});
    setSaveError('');
    setUploadError('');
    setAutofillWarnings([]);
    setPassportPreviewUrl('');
    setPassportPreviewType('');
  }

  function openAddDialog() {
    resetDialogState(hasMyselfMember ? 'Spouse' : 'Myself');
    setDialogOpen(true);
  }

  function openEditDialog(member) {
    if (passportPreviewUrl) {
      URL.revokeObjectURL(passportPreviewUrl);
    }

    setDialogMode('edit');
    setActiveMemberId(member.memberId);
    setForm(mapMemberToForm(member));
    setErrors({});
    setSaveError('');
    setUploadError('');
    setAutofillWarnings([]);
    setPassportPreviewUrl('');
    setPassportPreviewType('');
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    resetDialogState(hasMyselfMember ? 'Spouse' : 'Myself');
  }

  function handleFieldChange(field, value) {
    setForm((currentForm) => {
      const nextForm = { ...currentForm, [field]: value };
      return field === 'relationship' && !isEditing
        ? autofillMyselfContact(nextForm, account)
        : nextForm;
    });
    setErrors((currentErrors) => ({ ...currentErrors, [field]: '' }));
  }

  async function handlePassportSelected(file) {
    if (!account?.token) {
      return;
    }

    const nextPreviewUrl = URL.createObjectURL(file);
    if (passportPreviewUrl) {
      URL.revokeObjectURL(passportPreviewUrl);
    }
    setPassportPreviewUrl(nextPreviewUrl);
    setPassportPreviewType(file.type || '');

    setUploadBusy(true);
    setParseBusy(false);
    setUploadError('');
    setAutofillWarnings([]);
    setErrors((currentErrors) => ({ ...currentErrors, passportUploadObjectKey: '' }));

    try {
      const upload = await createPassportUpload({
        token: account.token,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
      });

      await uploadFileToPresignedUrl({
        uploadUrl: upload.uploadUrl,
        file,
        contentType: file.type || 'application/octet-stream',
      });

      setForm((currentForm) => ({
        ...currentForm,
        passportUploadObjectKey: upload.objectKey,
        passportFileName: file.name,
      }));

      setUploadBusy(false);
      setParseBusy(true);

      const autofill = await parsePassportAutofill({
        token: account.token,
        objectKey: upload.objectKey,
      });

      setAutofillWarnings(autofill.warnings ?? []);
      const parsedPhone = splitPhoneNumber(autofill.phoneNumber);
      setForm((currentForm) => {
        const nextForm = {
          ...currentForm,
          passportUploadObjectKey: upload.objectKey,
          passportFileName: file.name,
          firstName: autofill.firstName ?? currentForm.firstName,
          middleNames: autofill.middleNames ?? currentForm.middleNames,
          lastName: autofill.lastName ?? currentForm.lastName,
          fullNamePerPassport: autofill.fullNamePerPassport ?? currentForm.fullNamePerPassport,
          nationality: autofill.nationality ?? currentForm.nationality,
          dateOfBirth: autofill.dateOfBirth ?? currentForm.dateOfBirth,
          placeOfBirth: autofill.placeOfBirth ?? currentForm.placeOfBirth,
          passportNumber: autofill.passportNumber ?? currentForm.passportNumber,
          passportIssueDate: autofill.passportIssueDate ?? currentForm.passportIssueDate,
          passportExpiryDate: autofill.passportExpiryDate ?? currentForm.passportExpiryDate,
          issuingCountry: autofill.issuingCountry ?? currentForm.issuingCountry,
          issuingAuthority: autofill.issuingAuthority ?? currentForm.issuingAuthority,
          isMachineReadableZonePresent:
            typeof autofill.isMachineReadableZonePresent === 'boolean'
              ? autofill.isMachineReadableZonePresent
              : currentForm.isMachineReadableZonePresent,
          passportParseConfidence:
            typeof autofill.confidence === 'number'
              ? autofill.confidence
              : currentForm.passportParseConfidence,
          passportParseWarnings: autofill.warnings ?? currentForm.passportParseWarnings,
          addressPerPassport: autofill.addressPerPassport ?? currentForm.addressPerPassport,
          countryCode: parsedPhone.countryCode || currentForm.countryCode,
          phoneLocalNumber: parsedPhone.phoneLocalNumber || currentForm.phoneLocalNumber,
          email: autofill.email ?? currentForm.email,
          genderPerPassport: autofill.genderPerPassport ?? currentForm.genderPerPassport,
          nationalIdNumber: autofill.nationalIdNumber ?? currentForm.nationalIdNumber,
          occupation: autofill.occupation ?? currentForm.occupation,
        };

        return autofillMyselfContact(nextForm, account);
      });
    } catch (error) {
      setUploadError(error.message);
    } finally {
      setUploadBusy(false);
      setParseBusy(false);
    }
  }

  async function handleSaveMember() {
    const nextErrors = validateMemberForm(form, hasMyselfConflict, {
      requirePassportUpload: !isEditing,
    });
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaveBusy(true);
    setSaveError('');

    try {
      const savedMember = isEditing
        ? await updateFamilyMember({
          token: account.token,
          memberId: activeMemberId,
          member: buildMemberPayload(form),
        })
        : await createFamilyMember({
          token: account.token,
          member: {
            ...buildMemberPayload(form),
            passportUploadObjectKey: form.passportUploadObjectKey,
            passportFileName: form.passportFileName,
          },
        });

      setMembers((currentMembers) =>
        isEditing
          ? currentMembers.map((member) =>
            member.memberId === savedMember.memberId ? savedMember : member
          )
          : [...currentMembers, savedMember]
      );
      setDialogOpen(false);
      resetDialogState(hasMyselfMember ? 'Spouse' : 'Myself');
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaveBusy(false);
    }
  }

  async function handleConfirmDelete() {
    if (!account?.token || !deleteTarget?.memberId) {
      return;
    }

    setDeleteBusy(true);
    setDeleteError('');

    try {
      await deleteFamilyMember({
        token: account.token,
        memberId: deleteTarget.memberId,
      });

      setMembers((currentMembers) =>
        currentMembers.filter((member) => member.memberId !== deleteTarget.memberId)
      );

      if (activeMemberId === deleteTarget.memberId) {
        setDialogOpen(false);
        resetDialogState(hasMyselfMember ? 'Spouse' : 'Myself');
      }

      setDeleteTarget(null);
    } catch (error) {
      setDeleteError(error.message);
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <Box sx={{ display: 'grid', gap: 2, gridColumn: '1 / -1' }}>
      {membersError ? <Alert severity="error">{membersError}</Alert> : null}
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, minmax(0, 1fr))',
            xl: 'repeat(3, minmax(0, 1fr))',
          },
        }}
      >
        {membersLoading ? (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              gridColumn: '1 / -1',
              minHeight: 94,
              px: 2,
            }}
          >
            <CircularProgress size={22} />
          </Box>
        ) : null}
        {members.map((member) => (
          <FamilyMemberCard
            key={member.memberId}
            member={member}
            onDelete={(nextMember) => {
              setDeleteError('');
              setDeleteTarget(nextMember);
            }}
            onEdit={openEditDialog}
          />
        ))}
        <AddFamilyMemberTile canAddMyself={!hasMyselfMember} onClick={openAddDialog} />
      </Box>

      <Dialog
        fullWidth
        maxWidth="lg"
        onClose={closeDialog}
        open={dialogOpen}
        PaperProps={{
          sx: {
            height: { xs: '100%', sm: 'min(880px, 92vh)' },
          },
        }}
      >
        <DialogTitle>{isEditing ? 'Edit family member' : 'Add family member'}</DialogTitle>
        <DialogContent
          sx={{
            display: 'grid',
            gap: 3,
            gridAutoRows: 'minmax(0, 1fr)',
            gridTemplateColumns: {
              xs: '1fr',
              md: isEditing ? '1fr' : 'minmax(0, 1.45fr) minmax(320px, 0.95fr)',
            },
            overflow: 'hidden',
            pb: 0,
          }}
        >
          <Box
            sx={{
              minHeight: 0,
              overflowY: 'auto',
              pb: 3,
              pt: 1,
            }}
          >
            <Stack spacing={1.5}>
              {saveError ? <Alert severity="error">{saveError}</Alert> : null}
              {showValidationHint ? (
                <Alert severity="error">
                  {missingRequiredFieldLabels.length > 0
                    ? `Some required fields are still missing: ${formatValidationSummary(missingRequiredFieldLabels)}.`
                    : 'Some fields still need attention before you can save this family member.'}
                </Alert>
              ) : null}
              {!isEditing && errors.passportUploadObjectKey ? (
                <Alert severity="warning">{errors.passportUploadObjectKey}</Alert>
              ) : null}

              <FieldText
                field="relationship"
                form={form}
                errors={errors}
                label="Relationship"
                onChange={handleFieldChange}
                select
              >
                {relationshipOptions.map((option) => (
                  <MenuItem
                    disabled={option === 'Myself' && hasMyselfConflict}
                    key={option}
                    value={option}
                  >
                    {option}
                  </MenuItem>
                ))}
              </FieldText>

              <FieldText
                field="maritalStatus"
                form={form}
                errors={errors}
                label="Marital status"
                onChange={handleFieldChange}
                select
              >
                {maritalStatusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </FieldText>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <FieldText field="nationalIdNumber" form={form} errors={errors} label="National ID number" onChange={handleFieldChange} />
                <FieldText field="occupation" form={form} errors={errors} label="Occupation" onChange={handleFieldChange} />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <FieldText field="fullNamePerPassport" form={form} errors={errors} label="Full name on passport" onChange={handleFieldChange} />
                <FieldText field="firstName" form={form} errors={errors} label="First name per passport" onChange={handleFieldChange} />
                <FieldText field="lastName" form={form} errors={errors} label="Last name per passport" onChange={handleFieldChange} />
              </Box>

              <FieldText field="middleNames" form={form} errors={errors} label="Middle name(s) per passport" onChange={handleFieldChange} />

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <FieldText field="dateOfBirth" form={form} errors={errors} label="Date of birth" onChange={handleFieldChange} type="date" InputLabelProps={{ shrink: true }} />
                <FieldText field="genderPerPassport" form={form} errors={errors} label="Gender per passport" onChange={handleFieldChange} select>
                  {genderOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </FieldText>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <FieldText field="nationality" form={form} errors={errors} label="Nationality" onChange={handleFieldChange} />
                <FieldText field="placeOfBirth" form={form} errors={errors} label="Place of birth" onChange={handleFieldChange} />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <FieldText field="passportNumber" form={form} errors={errors} label="Passport number" onChange={handleFieldChange} />
                <FieldText field="email" form={form} errors={errors} label="Email" onChange={handleFieldChange} type="email" />
              </Box>

              <PhoneNumberFields
                form={{ countryCode: form.countryCode, phoneLocalNumber: form.phoneLocalNumber }}
                minLocalLength={8}
                onChange={(phoneForm) => {
                  setForm((currentForm) => ({
                    ...currentForm,
                    countryCode: phoneForm.countryCode,
                    phoneLocalNumber: phoneForm.phoneLocalNumber,
                  }));
                  setErrors((currentErrors) => ({ ...currentErrors, phoneLocalNumber: '' }));
                }}
              />
              {errors.phoneLocalNumber ? (
                <Typography color="error" sx={{ mt: -1 }} variant="caption">
                  {errors.phoneLocalNumber}
                </Typography>
              ) : null}
              <FieldText field="addressPerPassport" form={form} errors={errors} label="Address per passport" onChange={handleFieldChange} multiline minRows={2} />

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <FieldText field="passportIssueDate" form={form} errors={errors} label="Passport issue date" onChange={handleFieldChange} type="date" InputLabelProps={{ shrink: true }} />
                <FieldText field="passportExpiryDate" form={form} errors={errors} label="Passport expiry date" onChange={handleFieldChange} type="date" InputLabelProps={{ shrink: true }} />
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gap: 1.5,
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                }}
              >
                <FieldText field="issuingCountry" form={form} errors={errors} label="Issuing country" onChange={handleFieldChange} />
                <FieldText field="issuingAuthority" form={form} errors={errors} label="Issuing authority" onChange={handleFieldChange} />
              </Box>
            </Stack>
          </Box>

          {!isEditing ? (
            <Box
              sx={{
                minHeight: 0,
                overflow: 'hidden',
                pb: { xs: 0, md: 3 },
                position: { xs: 'static', md: 'sticky' },
                top: { md: 0 },
                zIndex: 1,
              }}
            >
              <PassportDropzone
                autofillWarnings={autofillWarnings}
                onPickFile={handlePassportSelected}
                parseBusy={parseBusy}
                previewType={passportPreviewType}
                previewUrl={passportPreviewUrl}
                stagedFileName={form.passportFileName}
                uploadBusy={uploadBusy}
                uploadError={uploadError}
              />
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button disabled={saveBusy || uploadBusy || parseBusy} onClick={closeDialog} variant="text">
            Cancel
          </Button>
          <Button disabled={saveDisabled} onClick={handleSaveMember} variant="contained">
            {saveBusy ? 'Saving...' : isEditing ? 'Save changes' : 'Save member'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        onClose={() => {
          if (!deleteBusy) {
            setDeleteError('');
            setDeleteTarget(null);
          }
        }}
        open={Boolean(deleteTarget)}
      >
        <DialogTitle>Delete family member</DialogTitle>
        <DialogContent sx={{ pt: '10px !important' }}>
          <Stack spacing={1.5}>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
            <Typography variant="body2">
              Delete {formatMemberName(deleteTarget || {}) || 'this family member'} and all documents linked to their profile?
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            disabled={deleteBusy}
            onClick={() => {
              setDeleteError('');
              setDeleteTarget(null);
            }}
            variant="text"
          >
            Cancel
          </Button>
          <Button color="error" disabled={deleteBusy} onClick={handleConfirmDelete} variant="contained">
            {deleteBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default FamilyWorkspace;
